import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";

import Close from "@mui/icons-material/Close";
import Send from "@mui/icons-material/Send";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import moment from "moment";
import { formatWithOutSom } from "../Message";

import axios from "axios";
import {
  loadOtherServicesdb,
  requestOtherService,
  loadRequestedOtherServicesInpatient,
} from "../BaseURL";
import Swal from "sweetalert2";

const RequestOtherServiceDialog = ({ open, onClose, patient, sendstartDate }) => {
  const currentDate = moment().format("YYYY-MM-DD");

  const [services, setServices] = useState([]);
  const [requestedToday, setRequestedToday] = useState([]);

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [requestDate, setRequestDate] = useState(currentDate);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [serviceQuantities, setServiceQuantities] = useState({});


  // 🔥 Track spinner per service
  const [requestingServiceId, setRequestingServiceId] = useState(null);

  // ------------------------------------------
  // LOAD AVAILABLE SERVICES
  // ------------------------------------------
  useEffect(() => {
    if (open && patient?.id) {
      setRequestDate(currentDate);
      loadServices();
      loadRequestedServices();
    }
  }, [open, patient]);

  const loadServices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(loadOtherServicesdb, {
        params: { charge_status: "Manual" },
      });

      if (res.data.success) setServices(res.data.results || []);
    } catch (e) {
      console.error("Error loading services:", e);
    } finally {
      setLoading(false);
    }
  };



  const loadRequestedServices = async () => {
    
    try {
      const res = await axios.get(loadRequestedOtherServicesInpatient, {
        params: { patientId: patient.id, dat:sendstartDate , currentDate},
      });

      if (res.data.success) {
        setRequestedToday(res.data.results || []);
       
      }
    } catch (e) {
      console.error("Error loading requested services:", e);
    }
  };

  // ------------------------------------------
  // REQUEST SERVICE
  // ------------------------------------------
  const handleRequestService = async (srv, serviceId) => {
    if (!requestDate) {
      setSnackbarMessage("❌ Please select a request date");
      setSnackbarOpen(true);
      return;
    }

    if (moment(requestDate).isBefore(moment(sendstartDate))) {
      setSnackbarMessage(`❌ Date cannot be earlier than admission date (${sendstartDate})`);
      setSnackbarOpen(true);
      return;
    }

    if (moment(requestDate).isAfter(moment(currentDate))) {
      setSnackbarMessage("❌ Date cannot be in the future");
      setSnackbarOpen(true);
      return;
    }

     const alreadyRequested = requestedToday.some(
            (r) => r.service === srv.name && r.date === currentDate
          );
        
          if (alreadyRequested) {
            const result = await Swal.fire({
              title: "Duplicate Request",
              text: `${srv.name} has already been requested today. Do you want to request again?`,
              icon: "warning",
              showCancelButton: true,
              confirmButtonColor: "#3085d6",
              cancelButtonColor: "#d33",
              confirmButtonText: "Yes, Request Again",
              cancelButtonText: "No"
            });
        
            if (!result.isConfirmed) {
              return; // user cancelled
            }
          }

    setRequestingServiceId(serviceId);

    try {
      const res = await axios.post(requestOtherService, {
        patientId: patient.id,
        dat: requestDate,
        charge: srv.name,
        amount: srv.amount,
        doc_name: patient.currentUser,
        from_user: patient.currentUser,
        name: patient.fullName,
        department: "Doctor",
      });

      if (!res.data.success) {
        setSnackbarMessage("❌ " + res.data.message);
      } else {
        setSnackbarMessage(`✅ ${srv.name} requested successfully`);
        loadRequestedServices();
      }
      setSnackbarOpen(true);
    } catch (error) {
      console.error(error);
      setSnackbarMessage(`❌ Failed to request ${srv.name}`);
      setSnackbarOpen(true);
    } finally {
      setRequestingServiceId(null);
    }
  };

  // ------------------------------------------
  // UI
  // ------------------------------------------
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
           background: "linear-gradient(135deg, #19b934ff 0%, #19b934ff 100%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 700,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <RoomServiceIcon />
            Request Other Service
          </span>

          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
            
            <Grid item xs={12} md={8} size={6}>
              <TextField
                fullWidth
                placeholder="Search service..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ mt: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={4} size={6}>
              <TextField
                fullWidth
                type="date"
                label="Request Date"
                value={requestDate}
                onChange={(e) => setRequestDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>

          {loading && (
            <Typography sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Typography>
          )}

          {!loading && (
            <Paper sx={{ p: 1, mt: 2 }}>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                <TableHead>
                <TableRow>
                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: "bold" }}>Service</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: "bold" }}>Quantity</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: "bold" }}>Amount</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: "bold" }} align="center">
                    Request
                    </TableCell>
                </TableRow>
                </TableHead>

                <TableBody>
                {services
                    .filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))
                    .map((srv, idx) => {
                    const key = `${srv.name}-${idx}`;
                    const quantity = serviceQuantities[key] || 1;
                    const finalAmount = Number(srv.amount) * quantity;

                    return (
                        <TableRow key={key}>

                        {/* Service Name */}
                        <TableCell>{srv.name}</TableCell>

                        {/* Quantity Selector */}
                        <TableCell>
                            <TextField
                            type="number"
                            size="small"
                            value={quantity}
                            inputProps={{ min: 1 }}
                            onChange={(e) => {
                                const val = Math.max(1, Number(e.target.value));
                                setServiceQuantities((prev) => ({ ...prev, [key]: val }));
                            }}
                            sx={{ width: 70 }}
                            />
                        </TableCell>

                        {/* Auto-updated Amount */}
                        <TableCell>{formatWithOutSom(finalAmount)}</TableCell>

                        {/* Request Button */}
                        <TableCell align="center">
                            {requestingServiceId === key ? (
                            <CircularProgress size={24} />
                            ) : (
                            <IconButton
                                color="primary"
                                onClick={() =>
                                handleRequestService(
                                    {
                                    ...srv,
                                    amount: finalAmount,
                                    quantity: quantity,
                                    },
                                    key
                                )
                                }
                            >
                                <Send />
                            </IconButton>
                            )}
                        </TableCell>
                        </TableRow>
                    );
                    })}
                </TableBody>

                </Table>
              </TableContainer>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            color="error"
            onClick={onClose}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbarMessage.includes("❌") ? "error" : "success"}
          sx={{ fontWeight: 600 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RequestOtherServiceDialog;
